<template>
  <div class="row justify-content-md-center">
    <div class="col-xl-3"></div>
    <div class="col-xl-auto col-center">
      <Zmodalform
        v-if="modal"
        :ok-button="'Close'"
        @ok-clicked="setModal(undefined, undefined)"
      >
        <div class="col-sm-12">
          <p class="waiting-text">{{ modal.header }}</p>
        </div>
        <div class="col-sm-12">
          {{ modal.text }}
        </div>
      </Zmodalform>

      <div class="loading-cover" v-if="loading">
        <div class="my-auto mx-auto text-center">
          <h2 class="waiting-text">{{ loading }}</h2>
          <zloading class="my-auto"></zloading>
        </div>
      </div>

      <OwnerInfoPage
        :first_name_="fields.first_name"
        :last_name_="fields.last_name"
        :email_="fields.email"
        :phone_="fields.phone"
        :key="ownerTimestamp"
        v-if="step_name === 'owner'"
        @next-clicked="ownerNext"
      />
      <LocationDetailsPage
        :locations="fields.locations"
        :prices="prices"
        :error="setError"
        v-if="step_name === 'locations'"
        @back-clicked="back"
        @next-clicked="locationNext"
      />
      <CheckoutPage
        :cart="cart"
        v-if="step_name === 'checkout'"
        :custom-terms-init="fields.custom_terms"
        :loading="setLoading"
        :error="setError"
        :payment-enabled="paymentEnabled"
        :readonly-signature="readonlySignature"
        :fields="fields"
        @custom-terms="saveCustomTerms"
        @back-clicked="back"
        @finish-clicked="next"
      />
      <CompletePage
        v-if="step_name === 'complete'"
        :show-dashboard-url="showDashboardURLAtComplete"
        @new-clicked="newSignup"
      />
    </div>
    <div class="col-xl-3 order-first order-xl-4">
      <PromoCode v-if="step_name === 'checkout'" @apply-promo="applyPromo" />
    </div>
  </div>
</template>

<script>
import OwnerInfoPage from "@/pages/OwnerInfoPage.vue";
import LocationDetailsPage from "@/pages/LocationDetailsPage.vue";
import OurPricing from "@/components/OurPricing.vue";
import CheckoutPage from "@/pages/CheckoutPage.vue";
import PromoCode from "@/components/PromoCode.vue";
import CompletePage from "@/pages/CompletePage.vue";
import store from "@/store";
import Zloading from "@/zcomponents/zloading";
import { signupService } from "@/services/ZdashService";
import { sleep } from "@/helpers";
import Zmodalform from "@/zcomponents/ZModalForm";
import { ERROR_MESSAGE } from "@/constants";

const STEPS = ["owner", "locations", "checkout", "complete"];
const STEP_COMPLETE = STEPS.indexOf("complete");
export const STEP_CHECKOUT = STEPS.indexOf("checkout");
const START_STEP = 0;

export default {
  name: "HomeContainer",
  components: {
    Zmodalform,
    Zloading,
    OwnerInfoPage,
    LocationDetailsPage,
    OurPricing,
    CheckoutPage,
    PromoCode,
    CompletePage,
  },
  props: {
    id: String,
    paymentEnabled: Boolean,
    showDashboardURLAtComplete: Boolean,
    readonlySignature: Boolean,
    initStep: {
      default: START_STEP,
      type: Number,
    },
    newSignupUrl: String,
  },
  data() {
    return {
      cart: {
        items: [],
        total: 0,
      },
      loading: "",
      modal: undefined,
      step: this.initStep,
      ownerTimestamp: undefined,
      prices: [],
      fields: {
        first_name: undefined,
        last_name: undefined,
        email: undefined,
        phone: undefined,
        custom_terms: "",
        locations: [],
      },
    };
  },
  methods: {
    setLoading(text) {
      this.loading = text;
    },
    setError(text) {
      this.setModal(text, "Note");
    },
    setModal(text, header) {
      if (!text) {
        this.modal = undefined;
        return;
      }
      this.modal = {
        text,
        header,
      };
    },
    async load() {
      this.setLoading("Loading...");
      if (this.id) {
        store.signupId = this.id;
      }
      await store.load();
      if (store.signup) {
        this.fields = store.signup;
        if (store.signup.status === "SIGNED_UP") {
          this.step = STEP_COMPLETE;
        } else if (
          store.signup.status === "WAIT_LIST" &&
          !this.paymentEnabled
        ) {
          this.step = STEP_COMPLETE;
        }
      }
      this.prices = await signupService.prices();
      if (this.step === STEP_CHECKOUT) {
        this.cart = await store.get_cart();
      }
      // It's a hack for force reloading fields, Have no idea why it is not reactive...
      this.ownerTimestamp = Date.now();
      this.setLoading(undefined);
    },
    back() {
      window.scrollTo({ top: 0 });
      this.step -= 1;
    },
    next() {
      window.scrollTo({ top: 0 });
      this.step += 1;
      const stepName = STEPS[this.step];
      this.$router.push(`/signup-and-pay/${stepName}/`);
    },
    async save() {
      try {
        await store.save_signup(this.fields);
      } catch (e) {
        if (e.response && e.response.status === 400) {
          this.setError(JSON.stringify(e.response.data, null, 2));
          this.setLoading(undefined);
          return false;
        }
        this.setError(ERROR_MESSAGE.unknown);
        return false;
      }
      return true;
    },
    ownerNext(ownerInfo) {
      this.fields = Object.assign(this.fields, ownerInfo);
      this.next();
    },
    async locationNext(locations) {
      this.setLoading("Give us a few seconds...");
      this.fields.locations = locations;
      if (!(await this.save())) {
        this.setLoading(undefined);
        return;
      }
      this.cart = await store.get_cart();
      this.next();
      await sleep(1000);
      this.setLoading(undefined);
    },
    newSignup() {
      store.clear();
      this.id = undefined;
      this.load();
      this.step = START_STEP;
      this.$router.push(this.newSignupUrl);
    },
    async applyPromo(promocode) {
      this.setLoading("Applying Promo Code...");
      try {
        const data = await store.apply_promo(promocode);
        const message = data.message ? data.message : "Promo Code is applied!";
        this.setModal(message, "Great!");
      } catch (e) {
        if (e.response && e.response.status === 400) {
          if (e.response.data.detail) {
            this.setError(e.response.data.detail[0]);
          } else {
            this.setError(e.response.data);
          }
        } else {
          this.setError(ERROR_MESSAGE.unknown);
        }
      }
      this.cart = await store.get_cart();
      this.setLoading(undefined);
    },
    async saveCustomTerms(terms) {
      this.setLoading("Saving...");
      this.fields.custom_terms = terms;
      if (!(await this.save())) {
        return;
      }
      this.setLoading(undefined);
    },
  },
  mounted() {},
  created() {
    this.load();
    this.$router.push(`/signup-and-pay/owner/`);
  },
  computed: {
    step_name() {
      return STEPS[this.step];
    },
    routeStep() {
      return this.$route.params.step;
    },
  },
  watch: {
    routeStep() {
      this.step = STEPS.indexOf(this.routeStep);
    },
  },
};
</script>

<style scoped>
/* ========================================== */
/* ======== START - RESPONSIVE CSS ========== */
/* Fluid Sizing */
/* font-size: calc([minimum size] + ([maximum size] - [minimum size]) * ((100vw - [minimum viewport width]) / ([maximum viewport width] - [minimum viewport width]))); */
/* Small Screens & Mobile Devices (portrait phones, less than 576px) */
@media (max-width: 1199.99px) {
  .desktop-only {
    display: none !important;
  }

  button.placeholder.mobile-menu.menu-btn {
    visibility: hidden;
  }

  .partner-settings.nav-item {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .mobile-menu {
    display: block;
  }

  .menu-desktop {
    display: none;
  }
}

/* Extra large devices (large desktops, 1200px and up) */
@media (min-width: 1200px) {
  .mobile-menu {
    display: none;
  }

  .col-center {
    width: 696px;
  }

  .menu-desktop {
    display: block;
  }

  /* .col-left {}
  .col-center {
    width: 100%;
    min-width: 580px;
  }
  .col-right {
    width: 100%;
    min-width: 580px;
  } */
}

/* END of desktop */

@media (max-width: 1399px) and (min-width: 1199px) {
  .col-xl-3 {
    max-width: 20%;
  }
}

/* ========== END - RESPONSIVE CSS ========== */
/* ========================================== */
</style>
